import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layers, Plus, Eye, Edit2, Trash2 } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../../components/Tables/APITable";
import Button from "../../../components/Button/Button";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import Notification from "../../../components/Notification/Notification";
import { TableSkeleton } from "../../../components/Skeleton/StatsSkeleton";

/* ===================== SERVICES ===================== */
import { apiGet, apiDelete } from "../../../apiService";
import { can } from "../../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./AdminPlans.module.scss";

const AdminPlans = () => {
  const navigate = useNavigate();

  /* =========================
     STATE
  ========================== */
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  /* =========================
     HELPERS
  ========================== */
  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* =========================
     API CALLS
  ========================== */
  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await apiGet("catalog", "/admin/plans");
      if (res?.status === "success") {
        setPlans(res.data.plans || []);
      }
    } catch {
      showNotification("Failed to fetch plans", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      const res = await apiDelete("catalog", `/plans/${deleteId}`);
      if (res?.status === "success") {
        showNotification("Plan deleted successfully", "success");
        setDeleteId(null);
        fetchPlans();
      } else {
        showNotification(res?.message || "Delete failed", "error");
      }
    } catch {
      showNotification("Failed to delete plan", "error");
    } finally {
      setDeleting(false);
    }
  };

  /* =========================
     TABLE CONFIG
  ========================== */
  const columns = [
    { label: "Plan Name", key: "planName" },
    { label: "Price (₹)", key: "price" },
    { label: "Credits", key: "credits" },
    { label: "Popular", key: "isPopular" },
    { label: "Icon", key: "iconName" },
    { label: "Actions", key: "action" },
  ];

  const rows = plans.map((p) => ({
    ...p,
    price: `₹${p.price?.toLocaleString("en-IN")}`,
    credits: p.credits?.toLocaleString("en-IN"),
    isPopular: p.isPopular || "—",
    iconName: p.iconName || "—",
  }));

  /* =========================
     JSX
  ========================== */
  return (
    <div className={styles["admin-plans__page"]}>
      {notification && (
        <div className={styles["admin-plans__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["admin-plans__header"]}>
        <div className={styles["admin-plans__title-area"]}>
          <div className={styles["admin-plans__icon-wrapper"]}>
            <Layers size={24} strokeWidth={2.5} />
          </div>
          <div className={styles["admin-plans__title-group"]}>
            <h2 className={styles["admin-plans__title"]}>Plans</h2>
            <p className={styles["admin-plans__subtitle"]}>
              Manage subscription plans and pricing tiers.
            </p>
          </div>
        </div>

        {can("plan", "create") && (
          <Button
            text="Add Plan"
            icon={Plus}
            onClick={() => navigate("/admin/plans/create")}
          />
        )}
      </div>

      {/* MAIN CARD */}
      <div className={styles["admin-plans__card"]}>
        <div className={styles["admin-plans__action-bar"]}>
          <span className={styles["admin-plans__showing-text"]}>
            Showing {plans.length} plan{plans.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className={styles["admin-plans__table-wrapper"]}>
          {loading ? (
            <TableSkeleton columns={6} rows={4} />
          ) : (
            <APITable
              columns={columns}
              data={rows}
              renderCell={(row, key) => {
                /* POPULAR BADGE */
                if (key === "isPopular" && row.isPopular !== "—") {
                  const isPop =
                    String(row.isPopular).toLowerCase() === "yes" ||
                    row.isPopular === true;
                  return (
                    <span
                      className={`${styles["admin-plans__badge"]} ${
                        isPop
                          ? styles["admin-plans__badge--popular"]
                          : styles["admin-plans__badge--standard"]
                      }`}
                    >
                      {String(row.isPopular)}
                    </span>
                  );
                }

                /* ACTIONS */
                if (key === "action") {
                  return (
                    <div className={styles["admin-plans__action-cell"]}>
                      <button
                        className={styles["admin-plans__icon-btn"]}
                        onClick={() => navigate(`/admin/plans/view/${row.id}`)}
                        title="View Plan"
                        aria-label="View Plan"
                      >
                        <Eye size={18} strokeWidth={2.5} />
                      </button>
                      {can("plan", "update") && (
                        <button
                          className={styles["admin-plans__icon-btn"]}
                          onClick={() => navigate(`/admin/plans/edit/${row.id}`)}
                          title="Edit Plan"
                          aria-label="Edit Plan"
                        >
                          <Edit2 size={18} strokeWidth={2.5} />
                        </button>
                      )}
                      {can("plan", "delete") && (
                        <button
                          className={`${styles["admin-plans__icon-btn"]} ${styles["admin-plans__icon-btn--danger"]}`}
                          onClick={() => setDeleteId(row.id)}
                          title="Delete Plan"
                          aria-label="Delete Plan"
                        >
                          <Trash2 size={18} strokeWidth={2.5} />
                        </button>
                      )}
                    </div>
                  );
                }

                return row[key];
              }}
            />
          )}
        </div>
      </div>

      {/* DELETE CONFIRMATION */}
      <ConfirmModal
        open={Boolean(deleteId)}
        title="Are you sure you want to delete this plan?"
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        disabled={deleting}
      />
    </div>
  );
};

export default AdminPlans;
